import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close, DocumentScanner, Download, Percent, SortOutlined } from '@mui/icons-material';
import type { TooltipProps } from 'recharts';
import { LoadingSpinner } from 'c-components';
import { parse } from 'json2csv';
import ChartExport from 'c-reports/Components/ReportControlsWrapper/ChartExport';
import { NetworkRequestState } from 'c-data-layer';
import type { ReportControlsState } from 'c-reports/Components';
import { ReportChartWrapper, ReportControlsWrapper } from 'c-reports/Components';
import type { ChartReportPart, ReportDateTimeframe } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { formatNumber } from 'c-lib';
import ImpactsMapView from './ImpactsMapView';

const groupByFieldWidgetDataCache = new Map<string, any[]>();
const groupByFieldWidgetInflightCache = new Map<string, Promise<any[]>>();

const downloadDataFile = (name: string, content: string, mimetype: string) => {
    const dataUrl = `data:${mimetype};charset=utf-8,${encodeURIComponent(content)}`;
    const saveLink = document.createElement('a');
    saveLink.download = name;
    saveLink.href = dataUrl;
    saveLink.click();
};

export const getWidgetChartAnimationKey = (
    data: any[] | null | undefined,
    xAxisDataKey: string,
    parts: ChartReportPart[],
) => {
    const rows = Array.isArray(data) ? data : [];

    return `${rows.length}-${rows
        .map(row => [row?.[xAxisDataKey], ...parts.map(part => row?.[part.dataKey])].join(':'))
        .join('|')}`;
};

const limitTopBars = (data: any[], parts: ChartReportPart[], topBars?: number): any[] => {
    if (!topBars || !Array.isArray(data) || data.length <= topBars) return data;
    const deliveredPart = parts.find(p => p?.filterRole === 'delivered') ?? parts[0];
    const key = (deliveredPart?.dataKey as string) ?? 'value';
    return [...data]
        .sort((a, b) => Number(b?.[key] ?? 0) - Number(a?.[key] ?? 0))
        .slice(0, topBars);
};

type WidgetFilterOptionLabels = {
    expected: string;
    delivered: string;
    both: string;
};

export type GroupByFieldWidgetReportProps = {
    widgetId: string;
    reportType: ReportType.Line | ReportType.Bar;
    namePrefix: string;
    startDate?: Date;
    endDate?: Date;
    timeframe?: ReportDateTimeframe;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    filterOptionLabels?: WidgetFilterOptionLabels;
    exportData?: any[];
    exportDataTransform?: (rows: any[]) => any[];
    requestKey: string;
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    fetchData: (start: Date, end: Date, timeframe: ReportDateTimeframe) => Promise<any[]>;
    defaultShowNumbers?: boolean;
    allowPercentToggle?: boolean;
    height?: number;
    hideControls?: boolean;
    fillHeight?: boolean;
    percentOfTotal?: boolean;
    percentOfTotalLabel?: string;
    percentLabelDecimals?: number;
    controls?: ReportControlsState;
    onControlsChange?: (controls: ReportControlsState) => void;
    orientation?: 'vertical' | 'horizontal';
    allowAccumulateToggle?: boolean;
    initialSort?: 'asc' | 'desc' | 'alpha' | null;
    topBars?: number;
};

export const GroupByFieldWidgetReport: React.FC<GroupByFieldWidgetReportProps> = ({
    widgetId,
    reportType,
    namePrefix,
    startDate,
    endDate,
    timeframe,
    xAxisDataKey,
    parts,
    filterOptionLabels,
    exportData,
    exportDataTransform,
    requestKey,
    renderTooltipLabel,
    fetchData,
    defaultShowNumbers = false,
    allowPercentToggle = false,
    height = 500,
    hideControls = false,
    fillHeight = false,
    percentOfTotal = false,
    percentOfTotalLabel,
    percentLabelDecimals,
    controls,
    onControlsChange,
    orientation,
    allowAccumulateToggle,
    initialSort,
    topBars,
}) => {
    const [widgetData, setWidgetData] = useState<any[]>([]);
    const [loadingState, setLoadingState] = useState(NetworkRequestState.Idle);
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
        let isCancelled = false;

        if (!startDate || !endDate || timeframe == null) {
            return undefined;
        }

        const cacheKey = [
            requestKey,
            startDate.toISOString(),
            endDate.toISOString(),
            timeframe,
        ].join('-');
        const cachedData = groupByFieldWidgetDataCache.get(cacheKey);
        if (cachedData != null) {
            setWidgetData(cachedData);
            setLoadingState(NetworkRequestState.Success);
            return undefined;
        }

        setLoadingState(NetworkRequestState.InProgress);
        const currentFetchData = fetchDataRef.current;
        const inflightRequest =
            groupByFieldWidgetInflightCache.get(cacheKey) ??
            currentFetchData(startDate, endDate, timeframe);

        if (!groupByFieldWidgetInflightCache.has(cacheKey)) {
            groupByFieldWidgetInflightCache.set(cacheKey, inflightRequest);
        }

        inflightRequest
            .then(rows => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
                if (isCancelled) return null;
                groupByFieldWidgetDataCache.set(cacheKey, rows);
                setWidgetData(rows);
                setLoadingState(NetworkRequestState.Success);
                return null;
            })
            .catch(() => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
                if (isCancelled) return null;
                setWidgetData([]);
                setLoadingState(NetworkRequestState.Error);
                return null;
            });

        return () => {
            isCancelled = true;
        };
    }, [endDate, requestKey, startDate, timeframe]);

    const actualExportData = useMemo(
        () => exportData ?? exportDataTransform?.(widgetData),
        [exportData, exportDataTransform, widgetData],
    );
    const chartData = useMemo(
        () => limitTopBars(widgetData, parts, topBars),
        [widgetData, parts, topBars],
    );

    const chartProps = {
        xAxisDataKey,
        data: chartData,
        parts,
        renderTooltipLabel,
        filterOptionLabels,
        exportData: actualExportData,
        allowPercentToggle,
        hideBrush: hideControls,
        hideLegend: hideControls,
        percentOfTotal,
        percentOfTotalLabel,
        percentLabelDecimals,
        orientation,
    };
    const chartAnimationKey = getWidgetChartAnimationKey(chartData, xAxisDataKey, parts);

    return (
        <Box
            id={widgetId}
            width="100%"
            position="relative"
            {...(fillHeight
                ? { sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } }
                : { minHeight: height })}
        >
            {loadingState === NetworkRequestState.InProgress && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            <Box {...(fillHeight ? { sx: { flex: 1, minHeight: 0 } } : { height })}>
                <ReportControlsWrapper
                    chartProps={chartProps}
                    type={reportType}
                    namePrefix={namePrefix}
                    startDate={startDate ?? new Date()}
                    endDate={endDate ?? new Date()}
                    defaultShowNumbers={defaultShowNumbers}
                    hideControls={hideControls}
                    controls={controls}
                    onControlsChange={onControlsChange}
                    allowAccumulateToggle={allowAccumulateToggle}
                    initialSort={initialSort}
                >
                    <ReportChartWrapper
                        key={chartAnimationKey}
                        {...chartProps}
                        type={reportType}
                        loadingState={loadingState}
                        chartProps={{
                            margin: { top: 20, right: 30, left: 65, bottom: 20 },
                        }}
                    />
                </ReportControlsWrapper>
            </Box>
        </Box>
    );
};

export type StaticChartWidgetReportProps = {
    widgetId: string;
    reportType: ReportType.Line | ReportType.Bar;
    namePrefix: string;
    startDate?: Date;
    endDate?: Date;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    data: any[];
    loadingState: NetworkRequestState;
    filterOptionLabels?: WidgetFilterOptionLabels;
    exportData?: any[];
    exportDataTransform?: (rows: any[]) => any[];
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    defaultShowNumbers?: boolean;
    allowPercentToggle?: boolean;
    height?: number;
    hideControls?: boolean;
    fillHeight?: boolean;
    sliderWrapperProps?: Record<string, any>;
    percentOfTotal?: boolean;
    percentOfTotalLabel?: string;
    percentLabelDecimals?: number;
    controls?: ReportControlsState;
    onControlsChange?: (controls: ReportControlsState) => void;
    orientation?: 'vertical' | 'horizontal';
    allowAccumulateToggle?: boolean;
    initialSort?: 'asc' | 'desc' | 'alpha' | null;
    topBars?: number;
    sortDataKey?: string;
    /** Defaults to hiding the legend only when the controls are hidden. */
    hideLegend?: boolean;
};

export const StaticChartWidgetReport: React.FC<StaticChartWidgetReportProps> = ({
    widgetId,
    reportType,
    namePrefix,
    startDate,
    endDate,
    xAxisDataKey,
    parts,
    data,
    loadingState,
    filterOptionLabels,
    exportData,
    exportDataTransform,
    renderTooltipLabel,
    defaultShowNumbers = false,
    allowPercentToggle = false,
    height = 500,
    hideControls = false,
    fillHeight = false,
    sliderWrapperProps,
    percentOfTotal = false,
    percentOfTotalLabel,
    percentLabelDecimals,
    controls,
    onControlsChange,
    orientation,
    allowAccumulateToggle,
    initialSort,
    topBars,
    sortDataKey,
    hideLegend,
}) => {
    const actualExportData = useMemo(
        () => exportData ?? exportDataTransform?.(data),
        [data, exportData, exportDataTransform],
    );
    const chartData = useMemo(() => limitTopBars(data, parts, topBars), [data, parts, topBars]);
    const chartProps = {
        xAxisDataKey,
        sortDataKey,
        data: chartData,
        parts,
        renderTooltipLabel,
        filterOptionLabels,
        exportData: actualExportData,
        allowPercentToggle,
        hideBrush: hideControls,
        hideLegend: hideLegend ?? hideControls,
        hideDots: hideControls,
        sliderWrapperProps,
        percentOfTotal,
        percentOfTotalLabel,
        percentLabelDecimals,
        orientation,
    };
    const chartAnimationKey = getWidgetChartAnimationKey(chartData, xAxisDataKey, parts);

    return (
        <Box
            id={widgetId}
            width="100%"
            position="relative"
            {...(fillHeight
                ? { sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } }
                : { minHeight: height })}
        >
            {loadingState === NetworkRequestState.InProgress && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            <Box {...(fillHeight ? { sx: { flex: 1, minHeight: 0 } } : { height })}>
                <ReportControlsWrapper
                    chartProps={chartProps}
                    type={reportType}
                    namePrefix={namePrefix}
                    startDate={startDate ?? new Date()}
                    endDate={endDate ?? new Date()}
                    defaultShowNumbers={defaultShowNumbers}
                    hideControls={hideControls}
                    controls={controls}
                    onControlsChange={onControlsChange}
                    allowAccumulateToggle={allowAccumulateToggle}
                    initialSort={initialSort}
                >
                    <ReportChartWrapper
                        key={chartAnimationKey}
                        {...chartProps}
                        type={reportType}
                        loadingState={loadingState}
                        chartProps={{
                            margin: { top: 20, right: 30, left: 65, bottom: 20 },
                        }}
                    />
                </ReportControlsWrapper>
            </Box>
        </Box>
    );
};

export type MapWidgetReportProps = {
    widgetId: string;
    campaignId: number;
    data: any;
    mapDataType: any;
    mapDisplayMode: any;
    loadingState: NetworkRequestState;
    height?: number;
};

export const MapWidgetReport: React.FC<MapWidgetReportProps> = ({
    widgetId,
    campaignId,
    data,
    mapDataType,
    mapDisplayMode,
    loadingState,
    height = 500,
}) => (
    <Box id={widgetId} minHeight={height} width="100%" position="relative">
        {loadingState === NetworkRequestState.InProgress && (
            <Box
                position="absolute"
                top="50%"
                left="50%"
                zIndex={1}
                sx={{ transform: 'translate(-50%, -50%)' }}
            >
                <LoadingSpinner />
            </Box>
        )}
        <ImpactsMapView
            id={campaignId}
            data={data}
            mapDataType={mapDataType}
            mapDisplayMode={mapDisplayMode}
            height={height}
        />
    </Box>
);

export type OverlayBarsWidgetReportProps = {
    widgetId: string;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    // eslint-disable-next-line react/no-unused-prop-types
    data: any[];
    // eslint-disable-next-line react/no-unused-prop-types
    loadingState?: NetworkRequestState;
    height?: number;
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
};

export const OverlayBarsWidgetReport: React.FC<OverlayBarsWidgetReportProps> = ({
    widgetId,
    xAxisDataKey,
    parts,
    data,
    loadingState,
    height = 240,
    renderTooltipLabel,
}) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const deliveredPart = parts.find(p => p?.filterRole === 'delivered') ?? parts[0];
    const expectedPart =
        parts.find(p => p?.filterRole === 'expected') ?? parts.find(p => p !== deliveredPart);
    const deliveredKey = (deliveredPart?.dataKey as string) ?? 'value';
    const expectedKey = expectedPart?.dataKey as string | undefined;
    const deliveredLabel = ((deliveredPart?.labelOverride as string) ?? deliveredKey) || 'Actual';
    const expectedLabel = ((expectedPart?.labelOverride as string) ?? expectedKey) || 'Expected';

    const rows = useMemo(
        () =>
            (Array.isArray(data) ? data : [])
                .map(row => ({
                    label: String(row?.[xAxisDataKey] ?? '').trim(),
                    delivered: Number(row?.[deliveredKey] ?? 0),
                    expected: expectedKey != null ? Number(row?.[expectedKey] ?? 0) : 0,
                    raw: row,
                }))
                .filter(r => r.label !== ''),
        [data, xAxisDataKey, deliveredKey, expectedKey],
    );

    const max = useMemo(
        () =>
            rows.reduce(
                (acc, r) => Math.max(acc, r.delivered, expectedKey != null ? r.expected : 0),
                0,
            ),
        [rows, expectedKey],
    );

    const isLoading = loadingState === NetworkRequestState.InProgress;
    const showBothValues = expectedKey != null;

    return (
        <Box id={widgetId} height={height} width="100%" position="relative">
            {isLoading && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            {!isLoading && rows.length === 0 && (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No data
                    </Typography>
                </Box>
            )}
            {!isLoading && rows.length > 0 && (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        pt: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                            borderBottom: theme => `1px solid ${theme.palette.divider}`,
                        }}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        {rows.map((row, idx) => {
                            const deliveredPct = max > 0 ? (row.delivered / max) * 100 : 0;
                            const expectedPct =
                                max > 0 && showBothValues ? (row.expected / max) * 100 : 0;
                            const performancePct =
                                row.expected > 0
                                    ? Math.round((row.delivered / row.expected) * 100)
                                    : null;
                            const layers: { colour: string; height: number }[] = showBothValues
                                ? row.delivered >= row.expected
                                    ? [
                                          { colour: 'primary.main', height: deliveredPct },
                                          { colour: 'grey.500', height: expectedPct },
                                      ]
                                    : [
                                          { colour: 'grey.500', height: expectedPct },
                                          { colour: 'primary.main', height: deliveredPct },
                                      ]
                                : [{ colour: 'primary.main', height: deliveredPct }];

                            const sharedTooltip = renderTooltipLabel
                                ? renderTooltipLabel({
                                      active: true,
                                      label: row.label,
                                      payload: parts.map(part => ({
                                          dataKey: part.dataKey,
                                          name: part.dataKey as string,
                                          color: (part.barProps as any)?.fill,
                                          value: row.raw?.[part.dataKey as string],
                                          payload: row.raw,
                                      })),
                                  } as any)
                                : null;
                            const tooltipContent = sharedTooltip ?? (
                                <Box
                                    sx={{
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        boxShadow: 3,
                                        minWidth: 160,
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                                    >
                                        {row.label}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={2}
                                    >
                                        <Stack direction="row" alignItems="center" gap={0.75}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: 'primary.main',
                                                    borderRadius: 0.5,
                                                }}
                                            />
                                            <Typography variant="caption">
                                                {deliveredLabel}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {formatNumber(row.delivered)}
                                        </Typography>
                                    </Stack>
                                    {showBothValues && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={2}
                                            sx={{ mt: 0.25 }}
                                        >
                                            <Stack direction="row" alignItems="center" gap={0.75}>
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        bgcolor: 'grey.500',
                                                        borderRadius: 0.5,
                                                    }}
                                                />
                                                <Typography variant="caption">
                                                    {expectedLabel}
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {formatNumber(row.expected)}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {performancePct != null && (
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            gap={2}
                                            sx={{
                                                mt: 0.5,
                                                pt: 0.5,
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                Performance
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 700,
                                                    color:
                                                        performancePct >= 100
                                                            ? 'success.main'
                                                            : 'warning.main',
                                                }}
                                            >
                                                {performancePct}%
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            );

                            const isHovered = hoveredIndex === idx;
                            return (
                                <Tooltip
                                    key={row.label}
                                    title={tooltipContent}
                                    placement="top"
                                    open={isHovered}
                                    disableHoverListener
                                    disableTouchListener
                                    disableFocusListener
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: 'transparent',
                                                p: 0,
                                                maxWidth: 'none',
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        onMouseEnter={() => setHoveredIndex(idx)}
                                        sx={{
                                            flex: 1,
                                            height: '100%',
                                            position: 'relative',
                                            cursor: 'default',
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'center',
                                            minWidth: 4,
                                            backgroundColor: isHovered
                                                ? theme => theme.palette.action.hover
                                                : 'transparent',
                                            transition: 'background-color 80ms ease-out',
                                        }}
                                    >
                                        {layers.map((layer, layerIdx) => (
                                            <Box
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={layerIdx}
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: '12%',
                                                    right: '12%',
                                                    height: `${Math.max(
                                                        layer.height > 0 ? 2 : 0,
                                                        layer.height,
                                                    )}%`,
                                                    backgroundColor: layer.colour,
                                                    borderRadius: '2px 2px 0 0',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            mt: 0.5,
                        }}
                    >
                        {rows.map(row => (
                            <Box
                                key={`label-${row.label}`}
                                sx={{
                                    flex: 1,
                                    textAlign: 'center',
                                    minWidth: 4,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.7rem' }}
                                >
                                    {row.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export type OverlayBarsFetchingWidgetReportProps = OverlayBarsWidgetReportProps & {
    fetchData: (start: Date, end: Date, timeframe: ReportDateTimeframe) => Promise<any[]>;
    startDate?: Date;
    endDate?: Date;
    timeframe?: ReportDateTimeframe;
    requestKey: string;
};

export const OverlayBarsFetchingWidgetReport: React.FC<OverlayBarsFetchingWidgetReportProps> = ({
    widgetId,
    xAxisDataKey,
    parts,
    fetchData,
    requestKey,
    startDate,
    endDate,
    timeframe,
    height,
    renderTooltipLabel,
}) => {
    const [data, setData] = useState<any[]>([]);
    const [loadingState, setLoadingState] = useState(NetworkRequestState.Idle);
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
        let isCancelled = false;
        if (!startDate || !endDate || timeframe == null) return undefined;

        const cacheKey = [
            requestKey,
            startDate.toISOString(),
            endDate.toISOString(),
            timeframe,
        ].join('-');
        const cached = groupByFieldWidgetDataCache.get(cacheKey);
        if (cached != null) {
            setData(cached);
            setLoadingState(NetworkRequestState.Success);
            return undefined;
        }

        setLoadingState(NetworkRequestState.InProgress);
        const currentFetch = fetchDataRef.current;
        const inflight =
            groupByFieldWidgetInflightCache.get(cacheKey) ??
            currentFetch(startDate, endDate, timeframe);
        groupByFieldWidgetInflightCache.set(cacheKey, inflight);
        // eslint-disable-next-line promise/catch-or-return
        inflight
            .then(rows => {
                if (isCancelled) return null;
                groupByFieldWidgetDataCache.set(cacheKey, rows);
                setData(rows);
                setLoadingState(NetworkRequestState.Success);
                return null;
            })
            .catch(() => {
                if (isCancelled) return null;
                setLoadingState(NetworkRequestState.Error);
                return null;
            })
            .finally(() => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
            });

        return () => {
            isCancelled = true;
        };
    }, [endDate, requestKey, startDate, timeframe]);

    return (
        <OverlayBarsWidgetReport
            widgetId={widgetId}
            xAxisDataKey={xAxisDataKey}
            parts={parts}
            data={data}
            loadingState={loadingState}
            height={height}
            renderTooltipLabel={renderTooltipLabel}
        />
    );
};

export type LeaderboardFetchingWidgetReportProps = {
    widgetId: string;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    fetchData: (start: Date, end: Date, timeframe: ReportDateTimeframe) => Promise<any[]>;
    startDate?: Date;
    endDate?: Date;
    timeframe?: ReportDateTimeframe;
    requestKey: string;
    height?: number;
    topN?: number;
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    fillHeight?: boolean;
    initialSortDir?: 'asc' | 'desc';
    initialPercentOn?: boolean;
};

export const LeaderboardFetchingWidgetReport: React.FC<LeaderboardFetchingWidgetReportProps> = ({
    widgetId,
    xAxisDataKey,
    parts,
    fetchData,
    requestKey,
    startDate,
    endDate,
    timeframe,
    height,
    topN,
    renderTooltipLabel,
    fillHeight,
    initialSortDir,
    initialPercentOn,
}) => {
    const [data, setData] = useState<any[]>([]);
    const [loadingState, setLoadingState] = useState(NetworkRequestState.Idle);
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
        let isCancelled = false;
        if (!startDate || !endDate || timeframe == null) return undefined;

        const cacheKey = [
            requestKey,
            startDate.toISOString(),
            endDate.toISOString(),
            timeframe,
        ].join('-');
        const cached = groupByFieldWidgetDataCache.get(cacheKey);
        if (cached != null) {
            setData(cached);
            setLoadingState(NetworkRequestState.Success);
            return undefined;
        }

        setLoadingState(NetworkRequestState.InProgress);
        const currentFetch = fetchDataRef.current;
        const inflight =
            groupByFieldWidgetInflightCache.get(cacheKey) ??
            currentFetch(startDate, endDate, timeframe);
        groupByFieldWidgetInflightCache.set(cacheKey, inflight);
        // eslint-disable-next-line promise/catch-or-return
        inflight
            .then(rows => {
                if (isCancelled) return null;
                groupByFieldWidgetDataCache.set(cacheKey, rows);
                setData(rows);
                setLoadingState(NetworkRequestState.Success);
                return null;
            })
            .catch(() => {
                if (isCancelled) return null;
                setLoadingState(NetworkRequestState.Error);
                return null;
            })
            .finally(() => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
            });

        return () => {
            isCancelled = true;
        };
    }, [endDate, requestKey, startDate, timeframe]);

    return (
        <LeaderboardWidgetReport
            widgetId={widgetId}
            xAxisDataKey={xAxisDataKey}
            parts={parts}
            data={data}
            loadingState={loadingState}
            height={height}
            topN={topN}
            renderTooltipLabel={renderTooltipLabel}
            fillHeight={fillHeight}
            initialSortDir={initialSortDir}
            initialPercentOn={initialPercentOn}
        />
    );
};

export type LeaderboardWidgetReportProps = {
    widgetId: string;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    data: any[];
    loadingState?: NetworkRequestState;
    height?: number;
    topN?: number;
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    preserveOrder?: boolean;
    fillHeight?: boolean;
    initialSortDir?: 'asc' | 'desc';
    initialPercentOn?: boolean;
};

export const LeaderboardWidgetReport: React.FC<LeaderboardWidgetReportProps> = ({
    widgetId,
    xAxisDataKey,
    parts,
    data,
    loadingState,
    height = 240,
    topN = 7,
    renderTooltipLabel,
    preserveOrder = false,
    fillHeight = false,
    initialSortDir = 'desc',
    initialPercentOn = false,
}) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);
    const [percentOn, setPercentOn] = useState(initialPercentOn);
    const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
    const [graphOpen, setGraphOpen] = useState(false);
    const deliveredPart = parts.find(p => p?.filterRole === 'delivered') ?? parts[0];
    const expectedPart =
        parts.find(p => p?.filterRole === 'expected') ?? parts.find(p => p !== deliveredPart);
    const deliveredKey = (deliveredPart?.dataKey as string) ?? 'value';
    const expectedKey = expectedPart?.dataKey as string | undefined;
    const deliveredLabel = ((deliveredPart?.labelOverride as string) ?? deliveredKey) || 'Actual';
    const expectedLabel = ((expectedPart?.labelOverride as string) ?? expectedKey) || 'Expected';

    const { rows, deliveredTotal } = useMemo(() => {
        const cleaned = (Array.isArray(data) ? data : [])
            .map(row => {
                const label = String(row?.[xAxisDataKey] ?? '').trim();
                const delivered = Number(row?.[deliveredKey] ?? 0);
                const expected = expectedKey != null ? Number(row?.[expectedKey] ?? 0) : 0;
                return { label, delivered, expected, raw: row };
            })
            .filter(r => r.label !== '' && Number.isFinite(r.delivered));

        const total = cleaned.reduce((acc, r) => acc + (r.delivered > 0 ? r.delivered : 0), 0);

        // Preserve backend order for e.g. day-of-week in the small view; the
        // expanded view always honours the explicit asc/desc control.
        if (fillHeight || !preserveOrder) {
            cleaned.sort((a, b) =>
                sortDir === 'asc' ? a.delivered - b.delivered : b.delivered - a.delivered,
            );
        }
        return { rows: cleaned.slice(0, topN), deliveredTotal: total };
    }, [data, xAxisDataKey, deliveredKey, expectedKey, topN, preserveOrder, fillHeight, sortDir]);

    const leaderMax = useMemo(
        () =>
            rows.reduce(
                (acc, r) => Math.max(acc, r.delivered, expectedKey != null ? r.expected : 0),
                0,
            ),
        [rows, expectedKey],
    );

    const isLoading = loadingState === NetworkRequestState.InProgress;
    const showBothValues = expectedKey != null;

    const exportData = useMemo(
        () =>
            rows.map(r => {
                const entry: Record<string, any> = {
                    [xAxisDataKey]: r.label,
                    [deliveredLabel]: r.delivered,
                };
                if (showBothValues) entry[expectedLabel] = r.expected;
                if (r.expected > 0) {
                    entry.Performance = `${Math.round((r.delivered / r.expected) * 100)}%`;
                }
                if (deliveredTotal > 0) {
                    entry['Share of total'] = `${((r.delivered / deliveredTotal) * 100).toFixed(
                        1,
                    )}%`;
                }
                return entry;
            }),
        [rows, xAxisDataKey, deliveredLabel, expectedLabel, showBothValues, deliveredTotal],
    );

    const activeStyle = (isActive: boolean) =>
        isActive
            ? {
                  color: 'primary.main',
                  bgcolor: 'action.selected',
                  '&:hover': { bgcolor: 'action.selected' },
                  borderRadius: 1,
              }
            : undefined;

    const handleExport = (fmt: 'csv' | 'json') => {
        if (fmt === 'csv') {
            downloadDataFile(`${widgetId}.csv`, parse(exportData), 'text/csv');
        } else {
            downloadDataFile(`${widgetId}.json`, JSON.stringify(exportData, null, 2), 'text/json');
        }
        setExportAnchor(null);
    };

    // Render the leaderboard's underlying grouped data as a horizontal bar chart
    // for the "download graph" export dialog (image / PDF / configurable export).
    // Horizontal mirrors the leaderboard's ranked-bar layout.
    const graphExportChartProps = useMemo(
        () => ({
            xAxisDataKey,
            data: rows.map(r => r.raw),
            parts,
            exportData,
            orientation: 'horizontal' as const,
        }),
        [xAxisDataKey, rows, parts, exportData],
    );

    const controls = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                py: 0.5,
            }}
        >
            <Tooltip title="Sort Descending">
                <IconButton
                    size="small"
                    onClick={() => setSortDir('desc')}
                    aria-pressed={sortDir === 'desc'}
                    sx={activeStyle(sortDir === 'desc')}
                >
                    <SortOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Sort Ascending">
                <IconButton
                    size="small"
                    onClick={() => setSortDir('asc')}
                    aria-pressed={sortDir === 'asc'}
                    sx={activeStyle(sortDir === 'asc')}
                >
                    <SortOutlined sx={{ transform: 'scaleY(-1)' }} fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={percentOn ? 'Hide % share' : 'Show % share of total'}>
                <IconButton
                    size="small"
                    onClick={() => setPercentOn(v => !v)}
                    aria-pressed={percentOn}
                    sx={activeStyle(percentOn)}
                >
                    <Percent fontSize="small" />
                </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Download data (CSV / JSON)">
                <IconButton
                    size="small"
                    onClick={e => setExportAnchor(e.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(exportAnchor)}
                >
                    <DocumentScanner fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
            >
                <MenuItem onClick={() => handleExport('csv')}>Download CSV</MenuItem>
                <MenuItem onClick={() => handleExport('json')}>Download JSON</MenuItem>
            </Menu>
            <Tooltip title="Download graph (configuration)">
                <IconButton onClick={() => setGraphOpen(true)} size="small">
                    <Download fontSize="inherit" />
                </IconButton>
            </Tooltip>
            {graphOpen && (
                <Dialog
                    open={graphOpen}
                    onClose={() => setGraphOpen(false)}
                    fullScreen
                    PaperProps={{ sx: { width: '100vw', height: '100vh' } }}
                >
                    <DialogContent sx={{ p: 2, height: '100%' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <ChartExport
                                type={ReportType.Bar}
                                chartProps={graphExportChartProps as any}
                                namePrefix={widgetId}
                                closeBtn={
                                    <IconButton onClick={() => setGraphOpen(false)}>
                                        <Close />
                                    </IconButton>
                                }
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );

    return (
        <Box
            id={widgetId}
            height={fillHeight ? '100%' : height}
            width="100%"
            position="relative"
            sx={fillHeight ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}
        >
            {fillHeight && controls}
            {isLoading && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            {!isLoading && rows.length === 0 && (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No data
                    </Typography>
                </Box>
            )}
            {!isLoading && rows.length > 0 && (
                <Stack spacing={0.25} sx={{ pt: 0.5 }} onMouseLeave={() => setHoveredIndex(null)}>
                    {rows.map((row, idx) => {
                        const deliveredPct =
                            leaderMax > 0
                                ? Math.max(
                                      row.delivered > 0 ? 2 : 0,
                                      (row.delivered / leaderMax) * 100,
                                  )
                                : 0;
                        const expectedPct =
                            leaderMax > 0 && showBothValues
                                ? Math.max(
                                      row.expected > 0 ? 2 : 0,
                                      (row.expected / leaderMax) * 100,
                                  )
                                : 0;
                        const performancePct =
                            row.expected > 0
                                ? Math.round((row.delivered / row.expected) * 100)
                                : null;
                        const sharePct =
                            deliveredTotal > 0 ? (row.delivered / deliveredTotal) * 100 : 0;
                        const EXPECTED_COLOUR = 'grey.500';
                        const ACTUAL_COLOUR = 'primary.main';
                        const layers: { colour: string; width: number }[] = showBothValues
                            ? row.delivered >= row.expected
                                ? [
                                      { colour: ACTUAL_COLOUR, width: deliveredPct },
                                      { colour: EXPECTED_COLOUR, width: expectedPct },
                                  ]
                                : [
                                      { colour: EXPECTED_COLOUR, width: expectedPct },
                                      { colour: ACTUAL_COLOUR, width: deliveredPct },
                                  ]
                            : [{ colour: ACTUAL_COLOUR, width: deliveredPct }];
                        const sharedTooltip = renderTooltipLabel
                            ? renderTooltipLabel({
                                  active: true,
                                  label: row.label,
                                  payload: parts.map(part => ({
                                      dataKey: part.dataKey,
                                      name: part.dataKey as string,
                                      color: (part.barProps as any)?.fill,
                                      value: row.raw?.[part.dataKey as string],
                                      payload: row.raw,
                                  })),
                              } as any)
                            : null;
                        const tooltipContent = sharedTooltip ?? (
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    color: 'text.primary',
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    boxShadow: 3,
                                    minWidth: 160,
                                    px: 1.5,
                                    py: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                                >
                                    {row.label}
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={2}
                                >
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                bgcolor: 'primary.main',
                                                borderRadius: 0.5,
                                            }}
                                        />
                                        <Typography variant="caption">{deliveredLabel}</Typography>
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                    >
                                        {formatNumber(row.delivered)}
                                    </Typography>
                                </Stack>
                                {showBothValues && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={2}
                                        sx={{ mt: 0.25 }}
                                    >
                                        <Stack direction="row" alignItems="center" gap={0.75}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: 'grey.500',
                                                    borderRadius: 0.5,
                                                }}
                                            />
                                            <Typography variant="caption">
                                                {expectedLabel}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {formatNumber(row.expected)}
                                        </Typography>
                                    </Stack>
                                )}
                                {performancePct != null && (
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        gap={2}
                                        sx={{
                                            mt: 0.5,
                                            pt: 0.5,
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Performance
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color:
                                                    performancePct >= 100
                                                        ? 'success.main'
                                                        : 'warning.main',
                                            }}
                                        >
                                            {performancePct}%
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        );
                        const isHovered = hoveredIndex === idx;
                        return (
                            <Tooltip
                                key={row.label}
                                title={tooltipContent}
                                placement="top"
                                open={isHovered}
                                disableHoverListener
                                disableTouchListener
                                disableFocusListener
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: 'transparent',
                                            p: 0,
                                            maxWidth: 'none',
                                        },
                                    },
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1.5}
                                    onMouseEnter={() => setHoveredIndex(idx)}
                                    sx={{
                                        cursor: 'default',
                                        py: 0.75,
                                        px: 0.75,
                                        mx: -0.75,
                                        borderRadius: 1,
                                        backgroundColor: isHovered
                                            ? theme => theme.palette.action.hover
                                            : 'transparent',
                                        transition: 'background-color 80ms ease-out',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 500,
                                        }}
                                        title={row.label}
                                    >
                                        {row.label}
                                    </Typography>
                                    <Box
                                        sx={{
                                            flex: 1.6,
                                            height: 8,
                                            backgroundColor: 'grey.100',
                                            borderRadius: 4,
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {layers.map((layer, idx) => (
                                            <Box
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={idx}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: `${layer.width}%`,
                                                    backgroundColor: layer.colour,
                                                    borderRadius: 4,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 52,
                                            textAlign: 'right',
                                            color: percentOn
                                                ? 'text.primary'
                                                : performancePct == null
                                                ? 'text.primary'
                                                : performancePct >= 100
                                                ? 'success.main'
                                                : 'warning.main',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {percentOn
                                            ? `${sharePct.toFixed(1)}%`
                                            : performancePct != null
                                            ? `${performancePct}%`
                                            : formatNumber(row.delivered)}
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
};
